if (!customElements.get('rdb-rose-discovery')) {
  customElements.define('rdb-rose-discovery', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.slides = [...this.querySelectorAll('.rfd-stack__slide')];
      if (this.slides.length < 2) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.index = 0;
      this.busy = false;
      this.animations = [];
      this.viewportWidth = window.innerWidth;
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.tabIndex = 0;
      this.querySelectorAll('img').forEach(image => { image.draggable = false; });
      this.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.addEventListener('pointerdown', event => this.startDrag(event), { signal });
      window.addEventListener('pointermove', event => this.moveDrag(event), { signal, passive: false });
      window.addEventListener('pointerup', event => this.endDrag(event), { signal });
      window.addEventListener('pointercancel', event => {
        if (event.pointerId === this.drag?.id) this.cancelDrag();
      }, { signal });
      this.addEventListener('lostpointercapture', event => {
        if (event.target === this && event.pointerId === this.drag?.id) this.cancelDrag();
      }, { signal });
      this.addEventListener('click', event => {
        if (performance.now() < this.suppressClickUntil && event.target.closest('.rfd-stack__slides')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressClickUntil = 0;
        }
      }, { signal, capture: true });
      this.querySelectorAll('[data-swipe-direction]').forEach(button => {
        button.addEventListener('click', () => this.go(Number(button.dataset.swipeDirection)), { signal });
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.drag) { this.cancelDrag(); return; }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.go(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.preference.addEventListener('change', () => {
        this.cancelDrag();
        this.animations.forEach(animation => animation.cancel());
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.cancelDrag();
          this.animations.forEach(animation => animation.cancel());
        }
      }, { signal });
      window.addEventListener('resize', () => {
        if (window.innerWidth === this.viewportWidth) return;
        this.viewportWidth = window.innerWidth;
        this.cancelDrag();
      }, { signal, passive: true });
      window.addEventListener('blur', () => this.cancelDrag(), { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0 && !this.busy) { this.cancelDrag(); this.index = index; this.layout(); this.announce(); }
      }, { signal });
      this.layout();
      this.announce();
      this.dataset.ready = '';
    }
    layout(direction = 1) {
      this.slides.forEach((slide, index) => {
        slide.dataset.depth = String(((index - this.index) * direction + this.slides.length) % this.slides.length);
        slide.inert = index !== this.index;
        if (index === this.index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
    }
    announce() {
      this.dataset.activeIndex = String(this.index);
      this.querySelector('.rfd-stack__count').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelector('[data-swipe-status]').textContent = this.dataset.positionLabel
        .replace('__current__', String(this.index + 1)).replace('__total__', String(this.slides.length))
        .replace('__title__', this.slides[this.index].dataset.title);
    }
    startDrag(event) {
      if (!event.isPrimary) { this.cancelDrag(); return; }
      this.suppressClickUntil = 0;
      if (this.busy || this.drag || (event.pointerType === 'mouse' && event.button !== 0) || event.target.closest('button')) return;
      const slide = event.target.closest('.rfd-stack__slide[data-depth="0"]');
      if (!slide) return;
      this.drag = { id: event.pointerId, slide, x: event.clientX, y: event.clientY, dx: 0,
        started: false, samples: [{ x: 0, at: event.timeStamp }], direction: 0,
        width: slide.getBoundingClientRect().width };
    }
    moveDrag(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.started) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) { this.releaseDrag(); return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
        drag.started = true;
        try { this.setPointerCapture(event.pointerId); }
        catch { this.cancelDrag(); return; }
        this.dataset.dragging = '';
        drag.slide.style.willChange = 'transform';
      }
      if (event.cancelable) event.preventDefault();
      drag.dx = dx;
      const now = event.timeStamp;
      drag.samples.push({ x: dx, at: now });
      while (drag.samples.length > 2 && drag.samples[1].at < now - 100) drag.samples.shift();
      const direction = dx < 0 ? 1 : -1;
      if (direction !== drag.direction) { drag.direction = direction; this.layout(direction); }
      const ratio = Math.max(-1.2, Math.min(1.2, dx / drag.width));
      const lift = this.preference.matches ? 0 : -Math.abs(ratio) * 14;
      const tilt = this.preference.matches ? 0 : ratio * 11;
      drag.slide.style.transform = `translate3d(${ratio * drag.width}px, ${lift}px, 0) rotate(${tilt}deg)`;
    }
    releaseDrag() {
      const drag = this.drag;
      this.drag = null;
      delete this.dataset.dragging;
      if (drag && this.hasPointerCapture(drag.id)) this.releasePointerCapture(drag.id);
      if (drag?.started) this.suppressClickUntil = performance.now() + 500;
      return drag;
    }
    endDrag(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const drag = this.drag;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.started && Math.abs(dx) >= 8 && Math.abs(dx) > Math.abs(dy) * 1.15) drag.started = true;
      if (drag.started) drag.dx = dx;
      this.releaseDrag();
      if (!drag.started) return;
      const distance = Math.abs(drag.dx), now = event.timeStamp;
      if (drag.dx !== drag.samples[drag.samples.length - 1].x) drag.samples.push({ x: drag.dx, at: now });
      const last = drag.samples[drag.samples.length - 1];
      const recent = drag.samples.find(sample => sample.at >= last.at - 100 && sample.at < last.at) || last;
      const velocity = now - last.at <= 100 ? (last.x - recent.x) / Math.max(1, last.at - recent.at) : 0;
      const speed = Math.sign(velocity) === Math.sign(drag.dx) ? Math.abs(velocity) : 0;
      if (distance >= 36 || (distance >= 18 && speed >= .15)) this.go(drag.dx < 0 ? 1 : -1);
      else this.restore(drag.slide);
    }
    cancelDrag() {
      const drag = this.releaseDrag();
      if (drag?.started) this.restore(drag.slide);
    }
    async restore(slide) {
      const connection = this.abort;
      this.busy = true;
      const transform = getComputedStyle(slide).transform;
      slide.style.removeProperty('transform');
      if (!this.preference.matches && !document.hidden && slide.animate) {
        this.animations = [slide.animate([{ transform }, { transform: 'none' }],
          { duration: 180, easing: 'cubic-bezier(.16,1,.3,1)' })];
        await Promise.allSettled(this.animations.map(animation => animation.finished));
      }
      if (this.abort !== connection || !this.isConnected) return;
      slide.style.removeProperty('will-change');
      this.animations = [];
      this.busy = false;
      this.layout();
    }
    async go(direction) {
      if (this.busy || this.drag) return;
      const connection = this.abort;
      this.busy = true;
      this.dataset.swiping = '';
      const outgoing = this.slides[this.index];
      const next = (this.index + direction + this.slides.length) % this.slides.length;
      const incoming = this.slides[next];
      const transferFocus = outgoing.contains(document.activeElement);
      this.layout(direction);
      const start = getComputedStyle(outgoing).transform;
      const behind = getComputedStyle(incoming).transform;
      outgoing.style.removeProperty('transform');
      if (!this.preference.matches && !document.hidden && outgoing.animate) {
        const width = outgoing.getBoundingClientRect().width;
        outgoing.style.willChange = incoming.style.willChange = 'transform';
        this.animations = [
          outgoing.animate([{ transform: start, opacity: 1 },
            { transform: `translate3d(${-direction * width * 1.2}px, -18px, 0) rotate(${-direction * 12}deg)`, opacity: 0 }],
            { duration: 220, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' }),
          incoming.animate([{ transform: behind }, { transform: 'none' }],
            { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' })
        ];
        await Promise.allSettled(this.animations.map(animation => animation.finished));
      }
      if (this.abort !== connection || !this.isConnected) return;
      this.index = next;
      this.layout();
      this.animations.forEach(animation => animation.cancel());
      this.animations = [];
      outgoing.style.removeProperty('will-change');
      incoming.style.removeProperty('will-change');
      this.busy = false;
      delete this.dataset.swiping;
      this.announce();
      if (transferFocus) (incoming.querySelector('a') || this).focus({ preventScroll: true });
    }
    state() {
      const slide = this.slides[this.index];
      return { version: 1, mode: 'browsing', activeCategory: slide.dataset.kind,
        selection: [{ key: slide.dataset.handle, unitPriceMinor: Number(slide.dataset.priceMinor), storefrontAccessible: slide.dataset.accessibleProduct === 'true' }],
        composition: { index: this.index, total: this.slides.length, title: slide.dataset.title }, size: null,
        totals: { currencyCode: 'AED', priceMinor: Number(slide.dataset.priceMinor) }, limits: { products: this.slides.length },
        validation: { valid: true, reasons: [] }, animation: { active: this.busy, phase: this.drag?.started ? 'dragging' : this.busy ? 'settling' : null, reducedMotion: this.preference.matches },
        persistence: { enabled: false, restored: false }, cart: { status: 'idle' }, shopifyPayload: { endpoint: null, items: [] } };
    }
    disconnectedCallback() {
      this.releaseDrag();
      this.abort?.abort();
      this.animations?.forEach(animation => animation.cancel());
      this.slides?.forEach(slide => {
        slide.inert = false;
        slide.removeAttribute('aria-hidden');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('will-change');
      });
      this.abort = null;
      this.removeAttribute('tabindex');
      delete this.dataset.ready;
    }
  });
  window.render_builder_to_text = () => document.querySelector('rdb-rose-discovery[data-ready]')?.state() || null;
  window.advanceTime = ms => {
    document.querySelectorAll('rdb-rose-discovery[data-ready]').forEach(stack => {
      stack.animations.forEach(animation => {
        const end = animation.effect.getComputedTiming().endTime;
        animation.currentTime = Math.min(end, (animation.currentTime || 0) + Math.max(0, ms));
        if (animation.currentTime >= end) animation.finish();
      });
    });
  };
}
